import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePresentation } from '../hooks/usePresentation.js';
import '../styles/screens.css';
import './Plans.css';

/**
 * Plans — the saved-presentations library.
 *
 * Empty state for new users; switches to a list once they have any
 * presentations. Cards show the plan title (editable inline), the
 * last-edited time, and "Continue →" / "Delete" actions.
 *
 * "Active" plan is whichever one the rest of the app (Home, the six
 * tools) is currently editing. Clicking Continue on a card sets that
 * plan as active and routes to Logistics so the user picks up where
 * they left off; clicking the active plan's card jumps straight there
 * without needing to switch.
 */
export default function Plans() {
  const navigate = useNavigate();
  const {
    plans,
    activeId,
    newPlan,
    openPlan,
    deletePlan,
    renamePlan,
  } = usePresentation();

  const [busy, setBusy] = useState(false);

  async function handleNewPlan() {
    if (busy) return;
    setBusy(true);
    const id = await newPlan();
    setBusy(false);
    if (id) navigate('/tools/logistics');
  }

  async function handleOpenPlan(id) {
    if (id !== activeId) await openPlan(id);
    navigate('/tools/logistics');
  }

  return (
    <div className="screen">
      <header className="screen-head">
        <div className="screen-head-text">
          <span className="eyebrow">My library</span>
          <h1>
            Your <em>presentations</em>
          </h1>
        </div>
        <button
          className="plans-add"
          aria-label="New presentation"
          type="button"
          onClick={handleNewPlan}
          disabled={busy}
        >
          +
        </button>
      </header>

      {plans.length === 0 ? (
        <div className="empty">
          <h2>Nothing here yet</h2>
          <p>
            Each presentation you start lives here, with auto-save and the
            full six-tool workbook ready to pick up from anywhere.
          </p>
          <button
            className="btn btn-primary"
            type="button"
            onClick={handleNewPlan}
            disabled={busy}
          >
            {busy ? 'Creating…' : 'Start your first plan'}
          </button>
        </div>
      ) : (
        <ul className="plans-list">
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              isActive={plan.id === activeId}
              onOpen={() => handleOpenPlan(plan.id)}
              onRename={(title) => renamePlan(plan.id, title)}
              onDelete={() => deletePlan(plan.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}


function PlanCard({ plan, isActive, onOpen, onRename, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(plan.title);
  const [confirming, setConfirming] = useState(false);

  function commit() {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== plan.title) onRename(trimmed);
    else setDraft(plan.title);
    setEditing(false);
  }

  return (
    <li className={`plan-card ${isActive ? 'is-active' : ''}`}>
      <div className="plan-card__head">
        {editing ? (
          <input
            className="plan-card__title-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') { setDraft(plan.title); setEditing(false); }
            }}
            autoFocus
          />
        ) : (
          <h3
            className="plan-card__title"
            onClick={() => { setDraft(plan.title); setEditing(true); }}
            title="Click to rename"
          >
            {plan.title}
          </h3>
        )}
        {isActive && <span className="pill pill-forest">Active</span>}
      </div>

      <div className="plan-card__meta">
        Last edited {formatRelative(new Date(plan.updatedAt))}
      </div>

      <div className="plan-card__actions">
        <button
          type="button"
          className="btn btn-secondary plan-card__open"
          onClick={onOpen}
        >
          {isActive ? 'Continue →' : 'Open →'}
        </button>
        {confirming ? (
          <span className="plan-card__confirm">
            Delete?{' '}
            <button
              type="button"
              className="btn-text danger"
              onClick={onDelete}
            >
              Yes
            </button>
            <button
              type="button"
              className="btn-text"
              onClick={() => setConfirming(false)}
            >
              Cancel
            </button>
          </span>
        ) : (
          <button
            type="button"
            className="btn-text danger plan-card__delete"
            onClick={() => setConfirming(true)}
          >
            Delete
          </button>
        )}
      </div>
    </li>
  );
}


/** Cheap "just now / 5 min ago / yesterday / 3 days ago" formatter. */
function formatRelative(date) {
  const ms = Date.now() - date.getTime();
  const min = Math.round(ms / 60000);
  if (min < 1)   return 'just now';
  if (min < 60)  return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24)   return `${hr} hr ago`;
  const d = Math.round(hr / 24);
  if (d === 1)   return 'yesterday';
  if (d < 30)    return `${d} days ago`;
  return date.toLocaleDateString('en-CA',
    { year: 'numeric', month: 'short', day: 'numeric' });
}
